import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { MdOutlineArrowBack } from "react-icons/md";
import { Table } from "react-bootstrap";
import PageContainer from "../../../components/PageContainer";
import RelatorioApiService from "../../../services/api/RelatorioApiService";
import Loading from "../../../components/Loading";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
    Line
} from "recharts";
import { FiInfo } from "react-icons/fi";

/** Utils */
const formatDateToYYYYMM = (date) => {
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
};

const formatDateToMMYYYY = (date) => {
    const dateObj = new Date(date);
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    return `${month}-${year}`;
};

const getMonthName = (anoMes) => {
    const [year, month] = anoMes.split("-");
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    return date.toLocaleDateString("pt-BR", { month: "long" }).replace(/^./, (c) => c.toUpperCase());
};

const formatPercent = (value) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return `${num.toFixed(2)}%`;
};

const MESES = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

/** Tooltip Customizado para o grafico */
const CustomTooltip = ({ active, payload, label, meta }) => {
    if (active && payload && payload.length) {
        return (
            <div
                className="custom-tooltip"
                role="tooltip"
                style={{
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    padding: "10px",
                    borderRadius: "5px"
                }}
            >
                <p><strong>Mês:</strong> {label}</p>
                <p><strong>Ocupação Faturável:</strong> {payload[0].value.toFixed(2)}%</p>
                <p><strong>Média Acumulada:</strong> {payload[1]?.value?.toFixed(2)}%</p>
                <p><strong>Meta:</strong> {parseFloat(meta)?.toFixed(2)}%</p>
            </div>
        );
    }
    return null;
};

const popupStyle = {
    position: 'absolute',
    top: '-250%',
    left: '-70%',
    transform: 'translateX(-55%)',
    backgroundColor: '#e9e9e9',
    color: '#212529',
    border: '1px solid #dee2e6',
    borderRadius: '5px',
    padding: '5px 10px',
    zIndex: 1000,
    whiteSpace: 'nowrap',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    width: 'auto',
    fontWeight: 'normal'
};

function RelatorioHistoricoCapacidadeView() {
    const history = useHistory();
    const location = useLocation();
    const { periodoInicial, periodoFinal } = location.state?.data || {};

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [relatorioData, setRelatorioData] = useState({});
    const [metaCapacidade, setMetaCapacidade] = useState(0);
    const [resumoCapacidade, setResumoCapacidade] = useState({});
    const [anoSelecionado, setAnoSelecionado] = useState("");
    const [visiblePopup, setVisiblePopup] = useState(null);

    const fetchRelatorioData = useCallback(async () => {
        if (!periodoInicial || !periodoFinal) {
            setError("Períodos inicial e final são obrigatórios");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const dataInicialFormatada = formatDateToYYYYMM(periodoInicial);
            const dataFinalFormatada = formatDateToYYYYMM(periodoFinal);

            const response = await RelatorioApiService.getRelatorioHistoricoCapacidade(
                dataInicialFormatada,
                dataFinalFormatada
            );

            const { result } = response.data;

            setRelatorioData(result.dadosRelatorioCapacidade);
            setResumoCapacidade(result.resumoCapacidade);
            
            const anos = Object.keys(result.dadosRelatorioCapacidade);
            if (anos.length > 0) {
                setAnoSelecionado((prev) => prev || anos[0]); // evita resetar se usuário já selecionou manualmente
            }
        } catch (err) {
            console.error("Erro ao buscar dados do relatório:", err);
            setError("Erro ao carregar dados do relatório. Tente novamente.");
        } finally {
            setLoading(false);
        }
    }, [periodoInicial, periodoFinal]);

    const fetchMetaCapacidade = useCallback(async () => {
        try {
            const meta = await RelatorioApiService.getMetaHistoricoCapacidade();

            setMetaCapacidade(meta.data.meta);
        } catch (err) {
            console.error("Erro ao buscar meta capacidade:", err);
            setError("Erro ao carregar dados do relatório, Tente novamente.")
        }
    }, []);

    useEffect(() => {
        fetchRelatorioData();
        fetchMetaCapacidade();
    }, [fetchRelatorioData, fetchMetaCapacidade]);

    // Obtém todos os anos disponíveis
    const anosDisponiveis = useMemo(() => {
        return Object.keys(relatorioData).sort();
    }, [relatorioData]);

    const dadosAnoSelecionado = useMemo(() => {
        if (!relatorioData[anoSelecionado]) return [];

        return Object.entries(relatorioData[anoSelecionado])
            .map(([mes, data]) => ({
                ...data,
                mesLabel: mes
            }))
            .sort((a, b) => MESES.indexOf(a.mesLabel) - MESES.indexOf(b.mesLabel));
    }, [relatorioData, anoSelecionado]);

    // Dados para o gráfico do ano selecionado
    const dataGrafico = useMemo(() => {
        return dadosAnoSelecionado.map(({ mesLabel, ocupacaoFaturavel, mediaAcumulada, meta }) => ({
            month: mesLabel,
            ocupacaoFaturavel: parseFloat(ocupacaoFaturavel) || 0,
            mediaAcumulada: parseFloat(mediaAcumulada) || 0,
            meta: parseFloat(meta) || 0
        }));
    }, [dadosAnoSelecionado]);

    const metaAnoSelecionado = useMemo(() => {
        const meses = Object.keys(relatorioData[anoSelecionado] || {});
        if (!meses.length) return 0;
        return relatorioData[anoSelecionado][meses[0]]?.meta || 0;
    }, [anoSelecionado, relatorioData]);

    const actions = useMemo(
        () => [
            {
                label: "Voltar",
                icon: <MdOutlineArrowBack />,
                color: "info",
                type: "button",
                onClick: () => history.goBack()
            }
        ],
        [history]
    );

    const title = `Relatório de Histórico de Capacidade - ${getMonthName(formatDateToYYYYMM(periodoInicial))}-${formatDateToMMYYYY(periodoInicial).split('-')[1]} a ${getMonthName(formatDateToYYYYMM(periodoFinal))}-${formatDateToMMYYYY(periodoFinal).split('-')[1]}`;

    if (!periodoInicial || !periodoFinal || error) {
        return (
            <PageContainer title="Relatório de Histórico de Capacidade" actions={actions}>
                <div className="alert alert-danger">
                    Parâmetros inválidos ou ausentes. Por favor, volte e selecione um período válido.
                </div>
            </PageContainer>
        );
    }
    

    return (
        <PageContainer title={title} actions={actions}>
            {loading ? (
                <Loading message="Carregando dados..." />
            ) : (
                <>
                    {Object.keys(relatorioData).length === 0 && (
                        <div className="alert alert-warning">
                            Nenhum dado disponível para o período selecionado.
                        </div>
                    )}

                    {Object.keys(relatorioData).length > 0 && (
                        <>
                            {/* Tabela de meta */}
                            <Table striped bordered hover className="mb-3 w-25">
                                <thead>
                                    <tr>
                                        <th>Meta Atual</th>
                                        <th style={{ color: "#ff7300" }}>
                                            {formatPercent(metaCapacidade)}
                                        </th>
                                    </tr>
                                </thead>
                            </Table>

                            <Table bordered className="mb-5 mt-5" style={{ background: "rgb(225, 234, 243)" }}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Total de Horas Obrigatórias
                                            <span
                                                onMouseEnter={() => setVisiblePopup('horas_obrigatorias')}
                                                onMouseLeave={() => setVisiblePopup(null)}
                                                className="ml-1 info-icon"
                                                style={{ position: 'relative', cursor: 'pointer' }}
                                            >
                                                <FiInfo />
                                                {visiblePopup === 'horas_obrigatorias' && (
                                                    <div className="hover-popup" style={popupStyle}>
                                                        Horas disponíveis sem overhead.
                                                    </div>
                                                )}
                                            </span>
                                        </th>
                                        <th>Total de Horas Faturadas
                                            <span
                                                onMouseEnter={() => setVisiblePopup('horas_faturadas')}
                                                onMouseLeave={() => setVisiblePopup(null)}
                                                className="ml-1 info-icon"
                                                style={{ position: 'relative', cursor: 'pointer' }}
                                            >
                                                <FiInfo />                                            
                                            {visiblePopup === 'horas_faturadas' && (
                                                <div className="hover-popup" style={popupStyle}>
                                                    Horas faturadas em projetos billables (incluidos os estagiarios).
                                                </div>
                                            )}
                                            </span>
                                        </th>
                                        <th>Porcentagem de Ocupação Faturável
                                            <span
                                                onMouseEnter={() => setVisiblePopup('ocupacao_faturavel')}
                                                onMouseLeave={() => setVisiblePopup(null)}
                                                className="ml-1 info-icon"
                                                style={{ position: 'relative', cursor: 'pointer' }}
                                            >
                                                <FiInfo />
                                                {visiblePopup === 'ocupacao_faturavel' && (
                                                    <div className="hover-popup" style={popupStyle}>
                                                        Porcentagem da ocupação dos colaboradores faturável para os clientes.
                                                    </div>
                                                )}
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><b>Total</b></td>
                                        <td><b>{resumoCapacidade.totalHorasObrigatorias.toLocaleString('pt-BR')} h</b></td>
                                        <td><b>{resumoCapacidade.totalHorasFaturaveis.toLocaleString('pt-BR')} h</b></td>
                                        <td><b>{resumoCapacidade.ocupacaoPercentual.toLocaleString('pt-br', { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b></td>
                                    </tr>
                                </tbody>
                            </Table>

                            {/* Listagem completa de todos os anos e meses */}
                            {anosDisponiveis.map((ano) => {
                                const meses = Object.keys(relatorioData[ano]);
                                const dadosAno = meses.map(mes => ({
                                    ...relatorioData[ano][mes],
                                    mesLabel: mes
                                })).sort((a, b) => {
                                    return MESES.indexOf(a.mesLabel) - MESES.indexOf(b.mesLabel);
                                });

                                return (
                                    <React.Fragment key={ano}>
                                        <h5><b>Ano:</b> {ano}</h5>
                                        <Table striped bordered hover className="mb-5">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Mês</th>
                                                    <th>Ocupação Faturável</th>
                                                    <th>Média Acumulada</th>
                                                    <th>Meta</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dadosAno.map((item, index) => (
                                                    <tr key={`${item.anoMes}-${item.ocupacaoFaturavel}-${item.mediaAcumulada}`}>
                                                        <td>{index + 1}</td>
                                                        <td>{item.mesLabel}</td>
                                                        <td>{formatPercent(item.ocupacaoFaturavel)}</td>
                                                        <td>{formatPercent(item.mediaAcumulada)}</td>
                                                        <td>{formatPercent(item.meta)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </React.Fragment>
                                );
                            })}

                            {/* Filtro de ano para o gráfico */}
                            <div className="w-100 d-flex justify-content-center mb-3">
                                <select
                                    style={{
                                        padding: "8px 16px",
                                        borderRadius: "10px",
                                        border: "1px solid #555",
                                        color: "#555",
                                        background: "transparent"
                                    }}
                                    value={anoSelecionado}
                                    onChange={(e) => setAnoSelecionado(e.target.value)}
                                >
                                    {anosDisponiveis.map((ano) => (
                                        <option key={ano} value={ano}>{ano}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Gráfico do ano selecionado */}
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={dataGrafico} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis tickFormatter={(value) => `${value.toFixed(2)}%`} />
                                    <Tooltip content={<CustomTooltip meta={dataGrafico[0].meta} />} />
                                    <Legend />
                                    <Bar dataKey="ocupacaoFaturavel" fill="#6265ea" name="Ocupação Faturável" />
                                    <Bar dataKey="mediaAcumulada" fill="#00c49f" name="Média Acumulada" />
                                    <ReferenceLine
                                        y={metaAnoSelecionado}
                                        stroke="#ff7300"
                                        strokeDasharray="4 4"
                                        strokeWidth={2}
                                        label={`Meta ${formatPercent(dataGrafico[0].meta)}`}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </>
                    )}
                </>
            )}
        </PageContainer>
    );
}

export default RelatorioHistoricoCapacidadeView;